import React, {useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import {BoxType, BoxTypeShape} from '@taopix/taopix-core';
import {Button, HelpTip, Horizontal, InfoIcon, Vertical} from '@taopix/taopix-design-system';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {useTaopix} from '../../Context/TaopixContext';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import {useExperience} from '../../Experience/ExperienceProvider';
import {PagesSelector} from '../../../Selectors';

type LayoutLockedTipTypes = {
  open: boolean;
  message: string;
};

type AddItemButtonProps = {
  pageNumber: number;
  boxType: BoxType;
  enabled: boolean;
  buttonProps: Partial<ButtonProps>;
  icon: React.ReactElement;
  label: string;
};

const AddItemButton = ({
  pageNumber,
  boxType,
  enabled,
  buttonProps,
  icon,
  label,
}: AddItemButtonProps) => {
  const ui = useUiState();
  const app = useTaopix();
  const {t} = useTranslation();
  const {experience} = useExperience();

  const addPictureTextBtnClicked = useRef<boolean>(false);
  const timeoutRef = useRef<any>(null);
  const isHovering = useRef<boolean>(false);

  const [layoutLockedTip, setLayoutLockedTip] = useState<LayoutLockedTipTypes>({
    open: false,
    message: '',
  });

  const pages = useSelector(PagesSelector);

  // memo our selected page
  const page = useMemo(() => {
    return pages.find(p => p.pagenumber === pageNumber);
  }, [pages, pageNumber]);

  const disableAddPictureTextButtons =
    !enabled || !(ui.state.canEditPageLayout && 0 === page?.locked);

  const onAddLayoutLockedTip = (event: any, type: BoxType) => {
    if (
      !disableAddPictureTextButtons ||
      !experience.EDITOR.pageEditing.allowUsersToToggleLocking ||
      ui.state.focusMode ||
      layoutLockedTip.open
    ) {
      event.preventDefault();
      return;
    }

    const message =
      BoxType.Image === type
        ? t('*:str_ToolTipAddPictureBoxNotAvailableEnableAdvanced')
        : t('*:str_ToolTipAddTextBoxNotAvailableEnableAdvanced');

    setLayoutLockedTip({
      open: true,
      message: message,
    });
  };

  const handleMouseEnter = (event, type: BoxType) => {
    isHovering.current = true;

    timeoutRef.current = setTimeout(() => {
      if (isHovering.current) onAddLayoutLockedTip(event, type);
    }, 500);
  };

  const handleMouseLeave = () => {
    isHovering.current = false;

    if (layoutLockedTip.open && !addPictureTextBtnClicked.current) {
      setLayoutLockedTip({
        ...layoutLockedTip,
        open: false,
      });
    }

    // clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const onAddNewPictureBox = () => {
    // Hide the autofill tip to avoid a clash of tips
    ui.dispatch(UserInterfaceActions.setTipOpen({name: 'useAutofill', open: false}));
    // Show the 'Apply a layout' tip
    ui.dispatch(
      UserInterfaceActions.setTipOpen({name: 'arrangePicturesByApplyingLayout', open: true})
    );
  };

  /**
   * Add a new text/picture box to the page
   *
   * @param event
   * @param type
   */
  const onAddNewItem = (event: React.MouseEvent, type: BoxType): void => {
    app.core
      .dispatch('PAGE.ADD_NEW_ITEM', {
        pageNumber: pageNumber,
        type: type,
        boxShape: BoxTypeShape.Rect,
        mouse: null,
        assetId: -1,
        assetType: 0,
        ignoreFixedLayout: true,
        event: event as unknown as MouseEvent,
        useSelectedSide: true,
        fuzzyPosition: true,
        preventSelect: true,
      })
      .then(() => {
        if (BoxType.Image === type) {
          onAddNewPictureBox();
        }
      })
      .catch(error => {
        console.error(error);
      });
  };

  const buttonSizingClasses = classNames('px-xs', 'shrink-0');

  const addPictureTextButtonClasses = classNames(
    ui.state.isSmallScreen && buttonSizingClasses,
    disableAddPictureTextButtons && 'pointer-events-none'
  );

  return (
    <>
      <div
        onClick={event => {
          addPictureTextBtnClicked.current = true;
          onAddLayoutLockedTip(event, boxType);
        }}
        onMouseEnter={event => handleMouseEnter(event, boxType)}
        onMouseLeave={handleMouseLeave}
        style={{
          cursor: disableAddPictureTextButtons ? 'not-allowed' : 'default',
        }}
        className={'sm:ml-xxs lg:ml-sm lg:mt-xs shrink-0'}
      >
        <Button
          id={'pageActionButton-AddPicture'}
          label={label}
          startIcon={icon}
          {...buttonProps}
          onClick={e => onAddNewItem(e, boxType)}
          disabled={disableAddPictureTextButtons}
          className={addPictureTextButtonClasses}
        />
      </div>
      <HelpTip
        key={`layoutLockedTip-${pageNumber}-${boxType}`}
        id={`layoutLockedTip-${pageNumber}-${boxType}`}
        open={layoutLockedTip.open}
        message={layoutLockedTip.message}
        anchorId={ui.state.isSmallScreen ? 'pageActionsPalette' : `pageActionButtons-${pageNumber}`}
        anchorOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Centre}}
        transformOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Centre}}
        offset={{x: 0, y: -80}}
        icon={<InfoIcon />}
        className={'max-w-[400rem]'}
        hideAfter={3000}
        onClose={() => {
          addPictureTextBtnClicked.current = false;
          setLayoutLockedTip({
            ...layoutLockedTip,
            open: false,
          });
        }}
      />
    </>
  );
};

export default AddItemButton;
